import { lazy } from 'react';

/**
 * sessionStorage key used to remember that we already forced a reload while
 * trying to recover from a failed chunk load, so we never reload more than once.
 */
const RELOAD_FLAG_KEY = 'chunk-load-reload-attempted';

function hasReloaded() {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');
    return true;
  } catch {
    return false;
  }
}

function clearReloaded() {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    // sessionStorage may be unavailable (private mode); nothing else to do.
  }
}

/**
 * Drop-in replacement for React.lazy that recovers from stale chunk errors.
 *
 * After a deployment the content-hashed chunk filenames change and the old
 * ones are removed from the host. A browser still running the previous
 * index.html requests a chunk that no longer exists; the SPA host answers with
 * index.html (Content-Type: text/html), so the browser refuses to run it
 * ("Refused to execute script ... MIME type ('text/html')") and the dynamic
 * import rejects. Left unhandled, that rejected React.lazy import blanks the
 * whole page.
 *
 * When the import fails we force a single full page reload so the browser
 * fetches the fresh index.html together with the new chunk filenames. A
 * sessionStorage flag guarantees we only reload once: a genuinely missing chunk
 * then surfaces the error (to an ErrorBoundary) instead of looping forever.
 *
 * @param {() => Promise<{ default: import('react').ComponentType }>} importFunction
 * @returns {import('react').LazyExoticComponent<import('react').ComponentType>}
 */
export default function lazyWithRetry(importFunction) {
  return lazy(async () => {
    try {
      const component = await importFunction();
      clearReloaded();
      return component;
    } catch (error) {
      if (!hasReloaded() && markReloaded()) {
        window.location.reload();
        return new Promise(() => {
          // Never resolves: keeps the Suspense fallback on screen until the
          // page reload takes over.
        });
      }
      throw error;
    }
  });
}
